import {
  Avatar,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import useAuth from "../hooks/useAuth";
import { MdCheck, MdClose, MdEdit } from "react-icons/md";
import { BsChevronDown, BsThreeDotsVertical } from "react-icons/bs";
import Dialog from "./Dialog/Dialog";
import RemoveComment from "./Dialog/RemoveComment";
import { useParams } from "react-router-dom";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "../config/axios";
import { toast } from "react-toastify";

function getRandomColor(n) {
  const colors = ["#EFD154", "#565656", "#B218A7", "#FE7B54"];
  return colors[Math.floor((n / 100) * colors.length)];
}

export const getVideoComments = async (videoId, page = 0, limit = 8) => {
  return axios
    .get(`comments/${videoId}?limit=${limit}&page=${page}`)
    .then((res) => {
      const comments = res.data;
      const hasNext = comments.length === limit;
      return {
        nextPage: hasNext ? page + 1 : undefined,
        previousPage: page > 1 ? page - 1 : undefined,
        comments,
      };
    });
};

export const postComment = ({ videoId, body }) => {
  return axios.post(`comments/${videoId}`, body);
};

export const updateComment = ({ commentId, body }) => {
  return axios.put(`comments/${commentId}`, body);
};

export default function Comments() {
  const { vidId } = useParams();
  const {
    data,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["comments", vidId],
    getNextPageParam: (prevData) => {
      return prevData.nextPage;
    },
    queryFn: async ({ pageParam = 0 }) => {
      return getVideoComments(vidId, pageParam);
    },
  });

  let content = null;

  if (isLoading)
    return <CircularProgress sx={{ display: "block", mx: "auto" }} />;

  if (isError) return <Typography>حدث خطأ ما</Typography>;

  if (data?.pages[0]?.comments?.length === 0) {
    content = (
      <Typography mt={3} fontWeight={300} textAlign="center">
        لا توجد تعليقات
      </Typography>
    );
  } else {
    let comments = data?.pages?.flatMap((data) => data?.comments);
    content = comments.map(({ id, user, text, myComment }, index) => {
      return (
        <Comment
          key={id}
          id={id}
          text={text}
          user={user}
          hasDivider={index < comments?.length - 1}
          myComment={true}
          commentId={id}
        />
      );
    });
  }

  return (
    <Stack mx={{ xs: 2, lg: 0 }}>
      <Typography variant="h6" fontWeight={500}>
        التعليقات
      </Typography>
      <CommentTF />
      {content}
      {isFetchingNextPage && (
        <CircularProgress sx={{ display: "block", mx: "auto" }} />
      )}
      {hasNextPage && !isFetchingNextPage && (
        <IconButton
          onClick={fetchNextPage}
          disabled={isFetchingNextPage}
          size="small"
          sx={{
            width: "100%",
            borderRadius: "0",
          }}
        >
          <BsChevronDown />
        </IconButton>
      )}
    </Stack>
  );
}

function CommentTF({ c = "", editingMode, handleClose, commentId }) {
  const [comment, setComment] = useState(c);
  const { phoneNumber } = useAuth();
  const { vidId } = useParams();
  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation({
    mutationFn: editingMode ? updateComment : postComment,
    onSuccess: () => {
      handleClose?.();
      setComment("");
      queryClient.invalidateQueries({ queryKey: ["comments", vidId] });
    },
  });

  const handleCheckClick = (e) => {
    if (comment) {
      let obj = { body: { text: comment } };
      if (editingMode) {
        obj.commentId = commentId;
      } else {
        obj.videoId = vidId;
      }
      mutate(obj);
    } else {
      toast.warn("يرجى إدخال تعليق لإكمال العملية");
    }
  };

  return (
    <TextField
      size="small"
      value={comment}
      sx={{ mb: editingMode ? 0 : 2, flexGrow: 1 }}
      variant={editingMode ? "standard" : "outlined"}
      onChange={(e) => setComment(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleCheckClick();
      }}
      inputProps={{ dir: "auto" }}
      InputProps={{
        startAdornment: !editingMode && (
          <Avatar
            sx={{
              width: 25,
              height: 25,
              fontSize: 14,
              bgcolor: getRandomColor(+phoneNumber?.slice(-2)),
            }}
          >
            {phoneNumber?.slice(-2)}
          </Avatar>
        ),
        endAdornment: (
          <Stack flexDirection="row">
            {isLoading ? (
              <CircularProgress
                size={16}
                sx={{ display: "block", m: "auto" }}
              />
            ) : (
              <IconButton
                size="small"
                onClick={handleCheckClick}
                disabled={comment === ""}
              >
                <MdCheck />
              </IconButton>
            )}
            <IconButton
              size="small"
              disabled={comment?.length === 0}
              onClick={() => {
                setComment("");
                handleClose?.();
              }}
            >
              <MdClose />
            </IconButton>
          </Stack>
        ),
      }}
    />
  );
}
function Comment({ text, user, hasDivider, myComment, commentId }) {
  const [editing, setEditing] = useState(false);

  return (
    <>
      <Stack flexDirection="row" gap={2} my={2}>
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: getRandomColor(+user),
            fontSize: 16,
          }}
        >
          {user}
        </Avatar>
        {editing ? (
          <CommentTF
            editingMode
            handleClose={() => setEditing(false)}
            c={text}
            commentId={commentId}
          />
        ) : (
          <Typography flexGrow={1}>{text}</Typography>
        )}

        {myComment && (
          <CommentMenu id={commentId} handleEdit={() => setEditing(true)} />
        )}
      </Stack>
      <Divider
        sx={{
          width: "100%",
          display: { xs: !hasDivider ? "none" : "block", lg: "block" },
        }}
      />
    </>
  );
}

function CommentMenu({ id, handleEdit }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <IconButton
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        size="small"
      >
        <BsThreeDotsVertical style={{ height: 16, width: 16 }} />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <Dialog
          openElement={
            <MenuItem onClick={handleClose} title="ازالة">
              <ListItemIcon>
                <MdClose />
              </ListItemIcon>
              <ListItemText>ازالة</ListItemText>
            </MenuItem>
          }
        >
          <RemoveComment commentId={id} />
        </Dialog>

        <MenuItem
          onClick={() => {
            handleEdit();
            handleClose();
          }}
          title="تعديل"
        >
          <ListItemIcon>
            <MdEdit />
          </ListItemIcon>
          <ListItemText>تعديل</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
}
